import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styles from './AdditionalInfoPopup.module.scss';
import { mustBeValidUrl } from '../Form/validators';
import Alert from '@spectrum-icons/workflow/Alert';

interface AdditionalInfoPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (formData: any) => void;
  onDelete?: () => void; // Add delete callback
  editMode?: boolean;
  initialValues?: { id?: string; description: string; url: string };
  issueId?: string; // Add issueId for API calls
  existingLinks?: Array<{ id: string; title: string; url: string }>; // Current links array for updating
}

interface AdditionalInfo {
  description: string;
  url: string;
}

const AdditionalInfoPopup: React.FC<AdditionalInfoPopupProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  editMode = false,
  initialValues,
  issueId,
  existingLinks = []
}) => {
  const [formValues, setFormValues] = useState<AdditionalInfo>({
    description: initialValues?.description || '',
    url: initialValues?.url || ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [urlError, setUrlError] = useState<string | undefined>(undefined);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Single source of truth for form state: always sync from initialValues,
  // including resetting to blank when it becomes falsy (switching to Add
  // mode). Previously this only handled the truthy (edit) case, so formValues
  // stayed populated with the last-edited/deleted link's data until one of
  // the close handlers (Save/Cancel/Delete) happened to clear it explicitly —
  // any other path back to Add mode would resurface stale data.
  useEffect(() => {
    setFormValues({
      description: initialValues?.description || '',
      url: initialValues?.url || ''
    });
  }, [initialValues]);

  const formRef = useRef<HTMLFormElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear URL error when user types
    if (name === 'url') {
      setUrlError(undefined);
    }
  };

  const validateUrl = (url: string): boolean => {
    // Treat empty as no error
    if (!url || url.trim() === '') {
      setUrlError(undefined);
      return false;
    }

    const error = mustBeValidUrl(url.trim());
    setUrlError(error);
    return !error;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Mark that a submit was attempted so we show required errors
    setHasSubmitted(true);

    // Check if required fields are empty
    const isDescriptionEmpty =
      !formValues.description || formValues.description.trim() === '';
    const isUrlEmpty = !formValues.url || formValues.url.trim() === '';

    // If required fields are empty, don't proceed
    if (isDescriptionEmpty || isUrlEmpty) {
      return;
    }

    // Validate URL format
    if (!validateUrl(formValues.url)) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Delegate the API call to the parent component via onSave
      if (onSave) {
        await onSave(formValues);
      }

      console.log('✅ Additional info updated successfully via parent handler');
    } catch (error) {
      console.error('❌ Error updating additional info:', error);
    } finally {
      setIsSubmitting(false);
      setHasSubmitted(false);
      setFormValues({
        description: '',
        url: ''
      });
      setUrlError(undefined);
      onClose();
    }
  };

  const handleCancel = () => {
    setFormValues({
      description: '',
      url: ''
    });
    setUrlError(undefined);
    setHasSubmitted(false);

    onClose();
  };

  const handleDelete = async () => {
    if (!editMode || !onDelete) return;

    setIsSubmitting(true);
    try {
      await onDelete();
      console.log('✅ Additional info deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting additional info:', error);
    } finally {
      setIsSubmitting(false);
      onClose();
      //Needed to reset Form values, so next time user won't see old data
      setFormValues({
        description: '',
        url: ''
      });
      setHasSubmitted(false);
    }
  };

  // Real-time validation check for Save button
  const isUrlValid = () => {
    // Treat empty or default prefix as invalid for submission
    if (
      !formValues.url ||
      formValues.url.trim() === '' ||
      formValues.url.trim() === 'https://www.'
    ) {
      return false;
    }
    const error = mustBeValidUrl(formValues.url.trim());
    return !error;
  };

  const isFormValid =
    formValues.description &&
    formValues.description.trim() !== '' &&
    formValues.url &&
    formValues.url.trim() !== '' &&
    isUrlValid();

  // Field-level invalid flags (match DACI popup UX)
  const isDescriptionInvalid =
    hasSubmitted &&
    (!formValues.description || formValues.description.trim() === '');
  const isUrlMissing =
    !formValues.url ||
    formValues.url.trim() === '' ||
    formValues.url.trim() === 'https://www.';
  const isUrlInvalid = hasSubmitted && (isUrlMissing || !!urlError);

  if (!isOpen) {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      className={styles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-info-title"
      data-testid="additional-info-popup-modal-overlay"
    >
      <div
        className={styles.popupContainer}
        data-testid="additional-info-popup-container"
      >
        <div
          className={styles.header}
          data-testid="additional-info-popup-header"
        >
          <h2
            id="add-info-title"
            className={styles.title}
            data-testid="additional-info-popup-title"
          >
            {editMode ? 'Edit' : 'Add Additional Info'}
          </h2>
        </div>

        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className={styles.form}
          data-testid="additional-info-popup-form"
        >
          <div
            className={styles.formGroup}
            data-testid="additional-info-popup-description-group"
          >
            <label
              htmlFor="description"
              className={`${styles.label} ${styles.required}`}
              data-testid="additional-info-popup-description-label"
            >
              Link Description
            </label>
            <div className={styles.inputWrapper}>
              <input
                type="text"
                id="description"
                name="description"
                value={formValues.description}
                onChange={handleInputChange}
                className={`${styles.input} ${
                  isDescriptionInvalid ? styles.error : ''
                } ${isDescriptionInvalid ? styles.withIcon : ''}`}
                placeholder="Enter Description"
                data-testid="additional-info-popup-description-input"
              />
              {isDescriptionInvalid && (
                <div className={styles.iconRight}>
                  <Alert
                    UNSAFE_className={styles.errorIcon}
                    aria-hidden="true"
                  />
                </div>
              )}
            </div>
            {isDescriptionInvalid && (
              <div className={styles.errorMessage}>Required Field</div>
            )}
          </div>

          <div
            className={styles.formGroup}
            data-testid="additional-info-popup-url-group"
          >
            <label
              htmlFor="url"
              className={`${styles.label} ${styles.required}`}
              data-testid="additional-info-popup-url-label"
            >
              URL
            </label>
            <div className={styles.inputWrapper}>
              <input
                type="text"
                id="url"
                name="url"
                value={formValues.url}
                onChange={handleInputChange}
                onBlur={e => validateUrl(e.target.value)}
                className={`${styles.input} ${
                  isUrlInvalid ? styles.error : ''
                } ${isUrlInvalid ? styles.withIcon : ''}`}
                placeholder="Enter URL"
                data-testid="additional-info-popup-url-input"
                autoComplete="off"
              />
              {isUrlInvalid && (
                <div className={styles.iconRight}>
                  <Alert
                    UNSAFE_className={styles.errorIcon}
                    aria-hidden="true"
                  />
                </div>
              )}
            </div>
            {isUrlInvalid && (
              <div
                className={styles.errorMessage}
                data-testid="additional-info-popup-url-error"
              >
                {isUrlMissing ? 'Required Field' : urlError}
              </div>
            )}
          </div>

          <div
            className={styles.actions}
            data-testid="additional-info-popup-actions"
          >
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleCancel}
              data-testid="additional-info-popup-cancel-button"
            >
              Cancel
            </button>

            {editMode && (
              <button
                type="button"
                className={styles.deleteButton}
                onClick={handleDelete}
                disabled={isSubmitting}
                data-testid="additional-info-popup-delete-button"
              >
                {isSubmitting ? 'Deleting...' : 'Delete'}
              </button>
            )}

            <button
              type="submit"
              className={styles.saveButton}
              disabled={isSubmitting}
              data-testid="additional-info-popup-save-button"
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default AdditionalInfoPopup;
